/* MG Pro — core JS. Exposes window.MG (see CONVENTIONS.md). No dependencies. */
(function () {
  'use strict';

  const MG = (window.MG = window.MG || {});
  MG.settings = Object.assign(
    {
      moneyFormat: '${{amount}}',
      routes: { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update', search: '/search' },
      cartType: 'drawer',
      cartGoals: [],
      canBuy: true,
      loginUrl: '/account/login',
      applyUrl: '/account/register',
      strings: {}
    },
    MG.settings || {}
  );
  const S = MG.settings;
  const str = (k, fallback) => (S.strings && S.strings[k]) || fallback;
  const root = (S.routes.root || '/').replace(/\/?$/, '/');

  /* ---------- Money ---------- */
  MG.money = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    format = format || S.moneyFormat || '${{amount}}';
    const delimit = (n, precision, thousands, decimal) => {
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      const fixed = (n / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount': return delimit(cents, 2);
        case 'amount_no_decimals': return delimit(cents, 0);
        case 'amount_with_comma_separator': return delimit(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(cents, 2, "'", '.');
        case 'amount_with_space_separator': return delimit(cents, 2, ' ', ',');
        default: return delimit(cents, 2);
      }
    });
  };

  /* ---------- Toast ---------- */
  let toastTimer;
  MG.toast = function (message, opts) {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('toast--error', !!(opts && opts.type === 'error'));
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), (opts && opts.duration) || 3200);
  };

  /* ---------- Fetch helpers ---------- */
  async function jsonFetch(url, options) {
    const res = await fetch(url, Object.assign({ headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }, options || {}));
    let data = null;
    try { data = await res.json(); } catch (e) { /* non-JSON */ }
    if (!res.ok || (data && data.status && Number(data.status) >= 400)) {
      const message = (data && (data.description || data.message)) || str('cartError', 'Something went wrong. Please try again.');
      throw { message: typeof message === 'string' ? message : str('cartError', 'Something went wrong. Please try again.'), status: res.status, data };
    }
    return data;
  }

  /* ---------- Cart ---------- */
  function emitCart(cart) {
    MG.cart.state = cart;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = !cart.item_count;
    });
    document.dispatchEvent(new CustomEvent('mg:cart:updated', { detail: cart }));
    return cart;
  }

  MG.cart = {
    state: null,
    get() {
      return jsonFetch(root + 'cart.js').then(emitCart);
    },
    async add(items, opts) {
      opts = Object.assign({ openDrawer: true }, opts || {});
      if (!Array.isArray(items)) items = [items];
      items = items
        .filter((i) => i && i.id && Number(i.quantity) > 0)
        .map((i) => {
          const o = { id: Number(i.id), quantity: Number(i.quantity) };
          if (i.properties && Object.keys(i.properties).length) o.properties = i.properties;
          if (i.selling_plan) o.selling_plan = i.selling_plan;
          return o;
        });
      if (!items.length) throw { message: str('nothingToAdd', 'Pick at least one item first.') };
      const added = await jsonFetch(root + 'cart/add.js', { method: 'POST', body: JSON.stringify({ items }) });
      const cart = await MG.cart.get();
      await MG.drawer.refresh();
      if (opts.openDrawer && S.cartType === 'drawer') MG.drawer.open();
      else if (opts.openDrawer && S.cartType === 'page') window.location.href = S.routes.cart;
      else if (opts.toast !== false) MG.toast(str('added', 'Added to cart'));
      return { added, cart };
    },
    async change(key, quantity) {
      const cart = await jsonFetch(root + 'cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity: Number(quantity) }) });
      emitCart(cart);
      await MG.drawer.refresh();
      return cart;
    },
    async update(updates, extra) {
      const body = Object.assign({ updates: updates || {} }, extra || {});
      const cart = await jsonFetch(root + 'cart/update.js', { method: 'POST', body: JSON.stringify(body) });
      emitCart(cart);
      await MG.drawer.refresh();
      return cart;
    },
    /** Total savings vs compare-at for lines that have one (cents). */
    savings(cart) {
      cart = cart || MG.cart.state;
      if (!cart) return 0;
      return cart.items.reduce((sum, l) => {
        const cap = l.compare_at_price || (l.variant && l.variant.compare_at_price) || 0;
        return cap > l.price ? sum + (cap - l.price) * l.quantity : sum;
      }, 0) + (cart.total_discount || 0);
    }
  };

  /** Apply a discount code to the cart (used by bundle/kit builders before checkout). */
  MG.applyDiscount = async function (code) {
    if (!code) return null;
    try {
      return await MG.cart.update({}, { discount: code });
    } catch (e) {
      await fetch(root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root + 'cart.js'), { credentials: 'same-origin' });
      return MG.cart.get();
    }
  };

  MG.goToCheckout = function () {
    window.location.href = root + 'checkout';
  };

  /* ---------- Generic drawers (menu, filters, cart) ---------- */
  const overlay = () => document.querySelector('[data-overlay]');
  let lastFocus = null;

  MG.openPanel = function (panel) {
    if (!panel) return;
    lastFocus = document.activeElement;
    document.querySelectorAll('.drawer.is-open').forEach((d) => d !== panel && MG.closePanel(d, true));
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    // A drawer rendered inside a section with its own stacking context (e.g. the sticky header) must sit above the overlay.
    const host = panel.closest('.shopify-section');
    if (host) host.classList.add('is-raised');
    const ov = overlay();
    if (ov) ov.classList.add('is-open');
    document.body.classList.add('is-locked');
    document.querySelectorAll('[aria-controls="' + panel.id + '"]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
    // Focus a marked field (e.g. the search input) or the panel itself, so no button shows a focus ring on open.
    const focusable = panel.querySelector('[data-autofocus]') || panel;
    if (!focusable.hasAttribute('tabindex') && focusable === panel) panel.setAttribute('tabindex', '-1');
    setTimeout(() => focusable.focus({ preventScroll: true }), 60);
  };

  MG.closePanel = function (panel, keepOverlay) {
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    const host = panel.closest('.shopify-section');
    if (host) setTimeout(() => { if (!host.querySelector('.drawer.is-open')) host.classList.remove('is-raised'); }, 320);
    document.querySelectorAll('[aria-controls="' + panel.id + '"]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    if (!keepOverlay) {
      const ov = overlay();
      if (ov) ov.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }
  };

  MG.drawer = {
    el() { return document.getElementById('CartDrawer'); },
    open() {
      if (S.cartType !== 'drawer' || !this.el()) { window.location.href = S.routes.cart; return; }
      MG.openPanel(this.el());
    },
    close() { MG.closePanel(this.el()); },
    async refresh() {
      const drawer = this.el();
      const pageCart = document.querySelector('[data-main-cart]');
      const ids = [];
      if (drawer) ids.push('cart-drawer');
      if (pageCart && pageCart.dataset.sectionId) ids.push(pageCart.dataset.sectionId);
      if (!ids.length) return;
      try {
        // Render from the cart URL when the cart page section is involved, so its template section id resolves.
        const base = pageCart && pageCart.dataset.sectionId ? S.routes.cart : root;
        const res = await fetch(base + (base.indexOf('?') > -1 ? '&' : '?') + 'sections=' + ids.join(','));
        const data = await res.json();
        if (drawer && data['cart-drawer']) swapInner(drawer, data['cart-drawer'], '[data-cart-drawer-inner]');
        if (pageCart && data[pageCart.dataset.sectionId]) swapInner(pageCart, data[pageCart.dataset.sectionId], '[data-main-cart-inner]');
      } catch (e) { /* keep current markup */ }
    }
  };

  function swapInner(scope, html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector(selector);
    const current = scope.querySelector(selector) || (scope.matches(selector) ? scope : null);
    if (next && current) current.innerHTML = next.innerHTML;
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-panel-open]');
    if (opener) {
      e.preventDefault();
      MG.openPanel(document.getElementById(opener.getAttribute('aria-controls') || opener.dataset.panelOpen));
      return;
    }
    const cartOpener = e.target.closest('[data-open-cart]');
    if (cartOpener && S.cartType === 'drawer' && MG.drawer.el()) {
      e.preventDefault();
      MG.drawer.open();
      return;
    }
    const closer = e.target.closest('[data-panel-close]');
    if (closer) {
      e.preventDefault();
      MG.closePanel(closer.closest('.drawer'));
      return;
    }
    if (e.target.matches('[data-overlay]')) {
      document.querySelectorAll('.drawer.is-open').forEach((d) => MG.closePanel(d));
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') document.querySelectorAll('.drawer.is-open').forEach((d) => MG.closePanel(d));
  });

  /* ---------- Cart line controls (drawer + cart page) ---------- */
  let lineBusy = false;
  async function changeLine(key, qty, scope) {
    if (lineBusy) return;
    lineBusy = true;
    scope && scope.classList.add('is-busy');
    try {
      await MG.cart.change(key, qty);
    } catch (err) {
      MG.toast(err.message, { type: 'error' });
      MG.drawer.refresh();
    } finally {
      lineBusy = false;
      scope && scope.classList.remove('is-busy');
    }
  }
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-line-remove]');
    if (rm) {
      e.preventDefault();
      changeLine(rm.dataset.lineRemove, 0, rm.closest('[data-line]'));
    }
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (input) changeLine(input.dataset.lineQty, Math.max(0, parseInt(input.value, 10) || 0), input.closest('[data-line]'));
  });

  /* ---------- <qty-input> ---------- */
  class QtyInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input || this._ready) return;
      this._ready = true;
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        e.preventDefault();
        const step = Number(this.input.step) || 1;
        const min = this.input.min !== '' ? Number(this.input.min) : 0;
        const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
        const cur = Number(this.input.value) || 0;
        let next = btn.hasAttribute('data-qty-plus') ? cur + step : cur - step;
        next = Math.min(max, Math.max(min, next));
        if (next === cur) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      });
      this.input.addEventListener('input', () => this.sync());
      this.input.addEventListener('change', () => this.sync());
      this.sync();
    }
    sync() {
      const min = this.input.min !== '' ? Number(this.input.min) : 0;
      const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
      const v = Number(this.input.value) || 0;
      const minus = this.querySelector('[data-qty-minus]');
      const plus = this.querySelector('[data-qty-plus]');
      if (minus) minus.disabled = v <= min;
      if (plus) plus.disabled = v >= max;
    }
  }
  if (!customElements.get('qty-input')) customElements.define('qty-input', QtyInput);

  /* ---------- <product-form> ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this._ready) return;
      this._ready = true;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const btn = this.form.querySelector('[type="submit"]');
      if (btn && btn.disabled) return;
      const fd = new FormData(this.form);
      const properties = {};
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && v !== '') properties[m[1]] = v;
      }
      const qty = Number(fd.get('quantity') || 1);
      const items = fd.getAll('id').map((id) => ({ id, quantity: qty, properties }));
      const item = items[0];
      btn && btn.classList.add('is-loading');
      try {
        await MG.cart.add(items, { openDrawer: this.dataset.openDrawer !== 'false' });
        this.dispatchEvent(new CustomEvent('mg:added', { bubbles: true, detail: item }));
      } catch (err) {
        MG.toast(err.message, { type: 'error' });
      } finally {
        btn && btn.classList.remove('is-loading');
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- Rotator (announcement bar) ---------- */
  document.querySelectorAll('[data-rotator]').forEach((el) => {
    const items = Array.from(el.querySelectorAll('[data-rotator-item]'));
    if (items.length < 2) return;
    let i = 0;
    const show = (n) => {
      i = (n + items.length) % items.length;
      items.forEach((it, idx) => {
        it.hidden = idx !== i;
        it.setAttribute('aria-hidden', idx !== i);
      });
    };
    const prev = el.querySelector('[data-rotator-prev]');
    const next = el.querySelector('[data-rotator-next]');
    prev && prev.addEventListener('click', () => show(i - 1));
    next && next.addEventListener('click', () => show(i + 1));
    const ms = Number(el.dataset.interval) || 0;
    let timer = ms ? setInterval(() => show(i + 1), ms) : null;
    el.addEventListener('mouseenter', () => timer && clearInterval(timer));
    el.addEventListener('mouseleave', () => { if (ms) timer = setInterval(() => show(i + 1), ms); });
    show(0);
  });

  /* ---------- Header scroll state ---------- */
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Init cart state ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
  });
  if (document.readyState !== 'loading') document.documentElement.classList.replace('no-js', 'js');
})();


/* Free gifts: the cart adds each earned gift by itself (line property _gift) and takes gift lines back out when the
   cart no longer earns them, so checkout never charges for a "free" gift. Gift amounts and products come from
   MG.settings.offers (snippets/offer-steps). A gift the pro removes by hand stays out (the cart's "Add free" row
   brings it back) until the order drops under that gift's amount or the cart is emptied. */
(() => {
  const MG = window.MG;
  if (!MG || !MG.settings || !MG.cart) return;
  const giftSteps = (MG.settings.offers || []).filter((s) => s.kind === 'gift' && s.productId);
  if (!giftSteps.length) return;
  const giftIds = new Set(giftSteps.map((s) => s.productId));
  const KEY = 'mg-gifts-added';
  const memory = {
    get() {
      try {
        return JSON.parse(window.sessionStorage.getItem(KEY) || '[]');
      } catch (e) {
        return [];
      }
    },
    set(ids) {
      try {
        window.sessionStorage.setItem(KEY, JSON.stringify(ids));
      } catch (e) {
        /* storage blocked: a removed gift may come back */
      }
    },
  };
  let busy = false;
  document.addEventListener('mg:cart:updated', async (e) => {
    const cart = e.detail;
    if (busy || !cart || !Array.isArray(cart.items) || cart.items_subtotal_price == null) return;
    const lines = cart.items.filter((l) => l.properties && l.properties._gift);
    // Gifts count the other lines at regular pro prices (before a sale), the way the gift discounts do; gift
    // products never count toward the gifts, added free or bought by hand.
    const giftLike = cart.items.filter((l) => (l.properties && l.properties._gift) || giftIds.has(l.product_id));
    const others = cart.items.length - giftLike.length;
    const base = cart.items.filter((l) => !giftLike.includes(l)).reduce((sum, l) => sum + (l.original_line_price != null ? l.original_line_price : l.final_line_price), 0);
    // Gifts added earlier stay remembered only while still earned, so they come back by themselves once re-earned.
    let added = memory.get().filter((id) => {
      const s = giftSteps.find((g) => g.productId === id);
      return others > 0 && !!s && base >= s.amount;
    });

    // 1. Keep one line per earned gift; anything else goes (unearned, duplicates, extra quantity, retired gifts).
    const updates = {};
    const kept = new Set();
    let lost = null;
    lines.forEach((l) => {
      const step = giftSteps.find((s) => s.productId === l.product_id);
      const earned = !!step && others > 0 && base >= step.amount;
      const keep = earned && !kept.has(l.product_id) ? 1 : 0;
      if (keep) kept.add(l.product_id);
      if (l.quantity !== keep) updates[l.key] = keep;
      if (step && !earned) lost = lost || step;
    });
    memory.set(added);
    if (Object.keys(updates).length) {
      busy = true;
      try {
        await MG.cart.update(updates);
        if (lost && others > 0) MG.toast(`${lost.title} removed: it's free on orders of ${MG.money(lost.amount)} or more.`);
      } catch (err) {
        /* leave the line; the cart shows it at full price */
      } finally {
        busy = false;
      }
      return;
    }

    // 2. Add each earned gift that isn't in the cart yet, unless the pro took it out by hand.
    if (!others || MG.settings.canBuy === false) return;
    // A gift bought by hand already gets the gift discount, so don't add a second one.
    const inCart = new Set(giftLike.map((l) => l.product_id));
    const due = giftSteps.filter((s) => s.variantId && s.available !== false && base >= s.amount && !inCart.has(s.productId) && !added.includes(s.productId));
    if (!due.length) return;
    added = added.concat(due.map((s) => s.productId));
    memory.set(added);
    busy = true;
    try {
      await MG.cart.add(due.map((s) => ({ id: s.variantId, quantity: 1, properties: { _gift: '1' } })), { openDrawer: false, toast: false });
      MG.toast(due.length > 1 ? 'Your free gifts were added to your cart' : `Free ${due[0].title} added to your cart`);
    } catch (err) {
      /* out of stock or refused: the cart's "Add free" row stays as the way to add it */
    } finally {
      busy = false;
    }
  });
  // Page loads with a gift to add (an "Add free" row) or to take out: run the same check once.
  const check = () => {
    if (document.querySelector('[data-gift-excess], .cart-gift')) MG.cart.get();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', check);
  else check();
})();
